import { makeContext } from "@shared/utils/makeContext";
import { Notification } from "./notifications.types";

// --- Name ---
const NAME = "notifications";

// --- Types ---
export type NotificationsState = {
  unreadCount: number;
  notifications: Notification[];
  isLoading: boolean;
  isRefetching: boolean;
  hasMore: boolean;
  page: number;
};

// --- Constants ---
export const NOTIFICATIONS_SET = "notifications/set" as const;
export const NOTIFICATIONS_SET_LOADING = "notifications/set/loading" as const;
export const NOTIFICATIONS_SET_REFETCHING =
  "notifications/set/refetching" as const;
export const NOTIFICATIONS_SET_HAS_MORE = "notifications/set/has-more" as const;
export const NOTIFICATIONS_SET_PAGE = "notifications/set/page" as const;
export const NOTIFICATIONS_REFRESH = "notifications/refresh" as const;
export const NOTIFICATION_SET_READ = "notifications/set/read" as const;

export const ACTION_TYPES = {
  NOTIFICATIONS_SET,
  NOTIFICATIONS_SET_LOADING,
  NOTIFICATIONS_SET_REFETCHING,
  NOTIFICATIONS_SET_HAS_MORE,
  NOTIFICATIONS_SET_PAGE,
  NOTIFICATION_SET_READ,
  NOTIFICATIONS_REFRESH,
} as const;

// --- Initial State ---
export const initialNotifications: NotificationsState = {
  unreadCount: 0,
  notifications: [],
  isLoading: false,
  isRefetching: false,
  hasMore: false,
  page: 1,
};

export type NotificationsAction = {
  type: string;
  payload?: Partial<NotificationsState>;
};

// --- Reducer ---
function notificationsReducer(
  state: NotificationsState,
  action: NotificationsAction
): NotificationsState {
  switch (action.type) {
    case NOTIFICATIONS_SET:
      return { ...state, ...action.payload };
    case NOTIFICATIONS_SET_LOADING:
      return { ...state, isLoading: action.payload?.isLoading ?? false };
    case NOTIFICATIONS_SET_REFETCHING:
      return { ...state, isRefetching: action.payload?.isRefetching ?? false };
    case NOTIFICATIONS_SET_HAS_MORE:
      return { ...state, hasMore: action.payload?.hasMore ?? false };
    case NOTIFICATIONS_SET_PAGE:
      return { ...state, page: action.payload?.page ?? 1 };
    case NOTIFICATIONS_REFRESH:
      return { ...state, unreadCount: action.payload?.unreadCount ?? 0 };
    default:
      throw new Error("Unknown action: " + action.type);
  }
}

// --- Context Creation ---
export const {
  Provider: NotificationsProvider,
  useState: useNotificationsState,
  useDispatch: useNotificationsDispatch,
} = makeContext<NotificationsState, NotificationsAction>({
  name: NAME,
  initialState: initialNotifications,
  reducer: notificationsReducer,
});
